import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormControl, Stack } from '@mui/material';
import { isAxiosError } from 'axios';

import { useGlobalErrorStore } from '@/components/dialogs';
import { CustomFormLabel, RHFMaskedPhoneTextField } from '@/components/form-controls';
import { StepsNavigation } from '@/pages/auth/components/StepsNavigation';
import {
  StyledFormDescription,
  StyledFormProvider,
  StyledFormRoot,
  StyledFormTitle,
} from '@/pages/auth/components/styles';
import { useSendOTP } from '@/pages/auth/components/VerifyPhone/hooks/useSendOTP';
import { useAuthMethods } from '@/shared/lib/auth';
import { AuthPaths } from '@/shared/lib/react-router';
import { useOTPStoreActions } from '@/shared/stores/otp.store';

import { getExpiredTimestamp, handleAxiosErrorOnTooManyRequests } from '../helpers/helpers';
import { useBan } from './hooks/useBan';
import { useNavigation } from './hooks/useNavigation';
import { useResetPasswordActions, useResetPasswordState } from './store/resetPassword.store';
import { accountSchema } from './validation-schema';

type FormValues = {
  phoneNumber: string;
};

export const AccountPage = () => {
  const navigate = useNavigate();
  const { totalSteps, goToNextStep } = useNavigation();
  const { setPhoneNumber } = useResetPasswordActions();
  const { phoneNumber } = useResetPasswordState();
  const { resetPasswordSendOTP, resetPasswordCheckIpLimits } = useAuthMethods();
  const { resetBan, applyBan } = useBan();
  const { setResetPasswordSendData, clearResetPasswordAttemptCounter, updateResetPasswordExpiredAt } =
    useOTPStoreActions();
  const { setGlobalError } = useGlobalErrorStore();
  const { t } = useTranslation('auth-reset-password');

  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    resolver: yupResolver(accountSchema),
    defaultValues: {
      phoneNumber,
    },
  });

  const {
    formState: { isSubmitting, isLoading },
    setError,
  } = methods;

  const checkIpLimits = async (lifetime: number) => {
    try {
      await resetPasswordCheckIpLimits();
      updateResetPasswordExpiredAt(getExpiredTimestamp(lifetime));
      goToNextStep();
    } catch (error) {
      if (isAxiosError(error)) {
        handleAxiosErrorOnTooManyRequests({
          error,
          handler: () => applyBan(parseInt(error?.response?.headers['retry-after'])),
        });
      }
    }
  };

  const sendOTP = useSendOTP({
    sendOtpApiMethod: resetPasswordSendOTP,
    onSuccess({ lifetime, sms_version }) {
      resetBan();
      setResetPasswordSendData({
        lifetime,
        expiredAt: getExpiredTimestamp(lifetime),
        sms_version,
      });
      clearResetPasswordAttemptCounter();
    },
  });

  const submitForm = async (formData: FormValues) => {
    const { phoneNumber } = formData;
    setPhoneNumber(formData.phoneNumber);

    try {
      await sendOTP({ phone: phoneNumber });
      goToNextStep();
    } catch (error) {
      if (isAxiosError(error)) {
        handleAxiosErrorOnTooManyRequests({
          error,
          handler: () => {
            checkIpLimits(parseInt(error?.response?.headers['retry-after']));
            return;
          },
        });
        if (error.response?.data?.message) {
          setError('phoneNumber', {
            message: error.response.data.message,
          });
          return;
        }
      }

      setGlobalError(error);
    }
  };

  const onBackwardClick = () => {
    navigate(AuthPaths.SIGN_IN.ROOT);
  };
  const isFormLoading = isSubmitting && isLoading;

  return (
    <StyledFormRoot>
      <StyledFormTitle>{t('account-page.form.title')}</StyledFormTitle>
      <StyledFormDescription>{t('account-page.form.description')}</StyledFormDescription>
      <StyledFormProvider methods={methods} onSubmit={submitForm}>
        <Stack spacing={3} mb={7}>
          <FormControl fullWidth>
            <CustomFormLabel htmlFor="phoneNumber" isRequired>
              {t('account-page.form.labels.phone-number')}
            </CustomFormLabel>
            <RHFMaskedPhoneTextField fullWidth name="phoneNumber" id="phoneNumber" variant="filled" />
          </FormControl>
        </Stack>
        <StepsNavigation
          onBackwardClick={onBackwardClick}
          currentStep={1}
          totalSteps={totalSteps}
          forwardButtonProps={{
            type: 'submit',
            loading: isFormLoading,
          }}
        />
      </StyledFormProvider>
    </StyledFormRoot>
  );
};
